import React, { useState, useEffect } from "react";
import { Box, Heading, Flex, Image, VStack, Input, Button, Text } from "@chakra-ui/react";
import ArrowIcon from "./Landing Page/ExpandArrow.svg"; 
import EditIcon from "../Images/edit-icon.svg"; 
import { BASE_API_URL } from './config';
import PaymentPlan from "./Landing Page/PaymentPlan";

const Settings = () => {
  const [expandedStep, setExpandedStep] = useState(null);
  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    dob: '',
  });
  const [isEditingField, setIsEditingField] = useState(null);
  const [activePlanGuid, setActivePlanGuid] = useState('');
  const [error, setError] = useState(null);

  const toggleExpand = (index) => {
    setExpandedStep(prev => (prev === index ? null : index));
  };

  useEffect(() => {
    const fetchUserData = async () => {
      const token = `Bearer ${localStorage.getItem('accessToken')}`;
      try {
        const response = await fetch(`${BASE_API_URL}/api/v1/users/own`, {
          headers: { Authorization: token },
        });
        if (!response.ok) throw new Error('Failed to fetch user data');
        const data = await response.json();
        setUserData({
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          dob: data.dob,
        });
        setActivePlanGuid(data.active_subscription_plan_guid || '');
      } catch (err) {
        setError(err.message);
      }
    };
    fetchUserData();
  }, []);

  const handleSave = async () => {
    const token = `Bearer ${localStorage.getItem('accessToken')}`;
    try {
      const response = await fetch(`${BASE_API_URL}/api/v1/users/own`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(userData),
      });
      if (!response.ok) throw new Error('Failed to save user data');
      setIsEditingField(null);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box>
      <Heading fontSize="24px" fontWeight="500" mb="16px">Settings</Heading>
      <VStack width={{ base: '100%', lg: '955px' }} spacing="20px">

        <Box
          height={expandedStep === 0 ? 'auto' : '70px'}
          transition="height 0.3s ease"
          p={expandedStep === 0 ? '13px 28px' : '15px 10px'}
          borderRadius="14px"
          border="1px solid var(--Primary, #F1F1F1)"
          width="100%"
        >
          <Flex justify="space-between" align="center" w="100%">
            <Heading fontSize="14px" fontWeight="400" color="#021A04">
              Personal Information
            </Heading>
            <Image
              src={ArrowIcon}
              alt="expand-collapse"
              onClick={() => toggleExpand(0)}
              cursor="pointer"
              transform={expandedStep === 0 ? 'rotate(180deg)' : 'rotate(0deg)'}
            />
          </Flex>

          {expandedStep === 0 && (
            <VStack pt="20px" spacing="8px">
              <Flex justify="space-between" w="100%">
                {['first_name', 'last_name'].map((field, idx) => (
                  <Flex key={idx} flexDirection="column" w="48%" align="flex-start" gap="5px">
                    <Flex align="center" gap="4px">
                      <Text fontSize="14px" fontWeight="400" color="#021A04">
                        {field === 'first_name' ? 'First Name' : 'Last Name'}
                      </Text>
                      <Image
                        src={EditIcon}
                        alt="edit"
                        cursor="pointer"
                        onClick={() => setIsEditingField(field)}
                      />
                    </Flex>
                    {isEditingField === field ? (
                      <Input
                        value={userData[field]}
                        onChange={(e) => setUserData({ ...userData, [field]: e.target.value })}
                        fontSize="10px"
                        fontWeight="300"
                        autoFocus
                      />
                    ) : (
                      <Text fontSize="12px" fontWeight="300" color="#021A04">
                        {userData[field]}
                      </Text>
                    )}
                  </Flex>
                ))}
              </Flex>

              <Flex justify="space-between" w="100%">
                <Flex flexDirection="column" w="100%" align="flex-start" gap="5px">
                  <Flex align="center" gap="4px">
                    <Text fontSize="14px" fontWeight="400" color="#021A04">Email</Text>
                    <Image
                      src={EditIcon}
                      alt="edit"
                      cursor="pointer"
                      onClick={() => setIsEditingField('email')}
                    />
                  </Flex>
                  {isEditingField === 'email' ? (
                    <Input
                      value={userData.email}
                      onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                      fontSize="14px"
                      fontWeight="300"
                      autoFocus
                    />
                  ) : (
                    <Text fontSize="12px" fontWeight="300" color="#021A04">
                      {userData.email}
                    </Text>
                  )}
                </Flex>
              </Flex>

              {isEditingField && (
                <Button 
                  variant="unstyled"
                  borderRadius="14px"
                  bg="#4BFF5C"
                  width="180px"
                  border="1px solid"
                  borderColor="#4BFF5C"
                  color="#021A04"
                  _hover={{
                    color: "#021A04",
                    borderColor: "#F1F1F1",
                    backgroundColor: "transparent",
                  }}
                  onClick={handleSave} 
                >
                  Save
                </Button>
              )}
              {error && <Text color="red.500" mt="8px">{error}</Text>}
            </VStack>
          )} 
        </Box>

        <Box
          height={expandedStep === 1 ? 'auto' : '70px'}
          transition="height 0.3s ease"
          p={expandedStep === 1 ? '13px 28px' : '15px 10px'}
          borderRadius="14px"
          border="1px solid var(--Primary, #F1F1F1)"
          width="100%"
        >
          <Flex justify="space-between" align="center" w="100%">
            <Heading fontSize="14px" fontWeight="400" color="#021A04">
              Account Plan
            </Heading>
            <Image
              src={ArrowIcon}
              alt="expand-collapse"
              onClick={() => toggleExpand(1)}
              cursor="pointer"
              transform={expandedStep === 1 ? 'rotate(180deg)' : 'rotate(0deg)'}
            />
          </Flex>

          {expandedStep === 1 && (
            <Box width="100%" pt="20px">
              <PaymentPlan activePlanGuid={activePlanGuid} />
            </Box>
          )}
        </Box>
      </VStack>
    </Box>
  );
};


export default Settings;